import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons'
import FloatCard from '../components/Card/FloatCard'
import PrimaryButton from '../components/Button/PrimaryButton'
import SearchBox from '../components/Input/SearchBox'
import SwitchTab from '../components/Navigation/SwitchTab'
import Pagination from '../components/Navigation/Pagination'
import { ANGGOTA_AKTIF_TABLE_HEADERS } from '../constants/table-header'
import { checkConnected } from '../utils/filter'
import KorwilSelect from '../components/KorwilSelect'
import PendaftarDetailModal from './PendaftarDetailModal'
import { saveAnggotaAKtif } from '../utils/exporter'
import RegistrasiModal from '../components/Modal/RegistrasiModal'
import { fetchAllActiveMembers, useGetAnggotaAktif } from '../hooks/useAnggotaAktif'
import ExportProgressModal from '../components/Modal/ExportProgressModal'
import { isCancelledError } from '../utils/error'
import { toast } from 'react-toastify'
import { formatNumber } from '../utils/formatter'
import PrintDataPage from '../components/PrintDataPage'
import PrintCSVMembercard from '../components/PrintCSVMembercard'

const AnggotaAktifTable = ({ rowsPerPage = 10 }) => {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('All')
  const [korwil, setKorwil] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [detail, setDetail] = useState(null)
  const [sortColumn] = useState(null)
  const [sortOrder] = useState(null)
  const [modal, setModal] = useState(false)
  // null = tidak sedang export. Selain itu { loaded, total } dari progress.
  const [exportProgress, setExportProgress] = useState(null)
  const exportAbortRef = useRef(null)
  const { data, isLoading } = useGetAnggotaAktif({
    query: search,
    pageSize: rowsPerPage,
    page: currentPage,
    status,
    korwil,
    sortColumn,
    sortOrder,
  })

  const isExporting = exportProgress !== null

  // Tabel cuma memuat satu halaman, jadi export mengambil ulang semua data
  // yang cocok dengan filter yang sedang aktif -- halaman demi halaman, dengan
  // progress dan tombol batal karena prosesnya bisa lama.
  const onExport = async () => {
    if (isExporting) {
      return
    }
    const controller = new AbortController()
    exportAbortRef.current = controller
    setExportProgress({ loaded: 0, total: 0 })
    try {
      const allRows = await fetchAllActiveMembers({
        query: search,
        status,
        korwil,
        sortColumnn: sortColumn,
        sortOrder,
        signal: controller.signal,
        onProgress: setExportProgress,
      })
      if (allRows.length === 0) {
        toast.info('Tidak ada data untuk diexport.')
        return
      }
      saveAnggotaAKtif(allRows)
    } catch (error) {
      if (isCancelledError(error)) {
        toast.info('Export dibatalkan.')
      } else {
        toast.error('Gagal mengexport data. Silakan coba lagi.')
      }
    } finally {
      exportAbortRef.current = null
      setExportProgress(null)
    }
  }

  const onCancelExport = () => {
    exportAbortRef.current?.abort()
  }

  // Kalau user pindah halaman waktu export masih jalan, request-nya ikut
  // dibatalkan supaya tidak menggantung.
  useEffect(
    () => () => {
      exportAbortRef.current?.abort()
    },
    [],
  )

  return (
    <>
      <FloatCard>
        <div className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-b border-line">
            <div>
              <div className="font-bold text-base text-ink">Anggota Aktif</div>
              <div className="text-[13px] text-muted">
                {isLoading
                  ? 'Memuat data...'
                  : `${formatNumber(data?.pagination?.total ?? 0)} anggota`}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <PrintCSVMembercard />
              {/* <PrintDataPage /> */}
              <PrimaryButton onClick={() => onExport()} loading={isExporting}>
                {isExporting ? 'Menyiapkan...' : 'Export CSV'}
                <FontAwesomeIcon icon={faCloudArrowDown} />
              </PrimaryButton>
            </div>
          </div>
          <div className="block px-4 py-3 lg:flex lg:justify-between lg:items-center lg:gap-3">
            <div className="flex-1 lg:max-w-[360px] lg:mb-0 mb-2">
              <SearchBox
                placeholder="Search dengan Nama atau Nomor KTA"
                value={search}
                onChange={setSearch}
              />
            </div>
            <div className="lg:mb-0 mb-2 lg:max-w-[240px]">
              <KorwilSelect value={korwil} onChange={setKorwil} />
            </div>
            <div className="overflow-auto">
              <SwitchTab
                tabs={[
                  { label: 'Semua', value: 'All' },
                  { label: 'Belum terkoneksi', value: 'Belum terkoneksi' },
                  { label: 'Terkoneksi', value: 'Terkoneksi' },
                  { label: 'Tidak Aktif', value: 'Tidak Aktif' },
                ]}
                activeTab={status}
                onChange={(v) => {
                  setCurrentPage(1)
                  setStatus(v)
                }}
              />
            </div>
          </div>
          <div>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    {ANGGOTA_AKTIF_TABLE_HEADERS.map((header) => (
                      <th
                        key={header.label}
                        onClick={
                          header.sortable
                            ? () => onChangeSort(header.value)
                            : null
                        }
                      >
                        {header.sortable ? (
                          <span style={{ cursor: 'pointer' }}>
                            {header.label}
                            {sortColumn === header.value &&
                              (sortOrder === 'asc' ? (
                                <span> &#x2191;</span>
                              ) : (
                                <span> &#x2193;</span>
                              ))}
                          </span>
                        ) : (
                          header.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={ANGGOTA_AKTIF_TABLE_HEADERS.length}
                        align="center"
                        className="!py-10"
                      >
                        <span className="loading loading-spinner loading-lg"></span>
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    data?.data.map((row, index) => (
                      <tr
                        key={index}
                        className="hover cursor-pointer"
                        onClick={() => {
                          // setDetail(row)
                        }}
                      >
                        {ANGGOTA_AKTIF_TABLE_HEADERS.map((header, i) => (
                          <td key={i}>
                            {header.format
                              ? header.format(row[header.value])
                              : row[header.value]}
                          </td>
                        ))}
                        <td>
                          <div
                            className="text-center bg-[#E3FCEF] text-[#006644] cursor-pointer"
                            onClick={() => HitNewCard(row.id)}
                          >
                            {row.pat}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="w-fit mx-auto sm:ml-auto sm:mr-0 px-4 pt-4 pb-2 border-t border-line">
            <Pagination
              totalPage={data?.pagination.pageCount ?? 0}
              currentPage={currentPage}
              onChange={setCurrentPage}
            />
          </div>
        </div>
      </FloatCard>
      <PendaftarDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        data={detail}
      />
      <RegistrasiModal open={modal} onClose={() => setModal(false)} />
      <ExportProgressModal
        open={isExporting}
        loaded={exportProgress?.loaded ?? 0}
        total={exportProgress?.total ?? 0}
        onCancel={onCancelExport}
      />
    </>
  )
}

export default AnggotaAktifTable
